import React from 'react';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, Link as LinkIcon } from 'lucide-react';
import { LinksAnalytics } from '../../services/linksService';
import { format, parseISO } from 'date-fns';

interface LinksMetricsProps {
  analytics: LinksAnalytics;
  totalLinks: number;
  totalClicks?: number;
}

const LinksMetrics: React.FC<LinksMetricsProps> = ({ analytics, totalLinks, totalClicks }) => {
  const { t } = useTranslation();

  // Format chart data
  const chartData = analytics.clicksByDay.map(item => ({
    date: format(parseISO(item.date), 'MMM dd'),
    clicks: item.clicks,
    uniqueUsers: item.uniqueUsers,
    fullDate: item.date
  }));

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-primary-600 rounded-full"></div>
              <span className="text-sm text-gray-600">
                {t('links.analytics.clicks', 'Clicks')}: <span className="font-medium">{payload[0]?.value}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-primary-300 rounded-full"></div>
              <span className="text-sm text-gray-600">
                {t('links.analytics.uniqueUsers', 'Unique Users')}: <span className="font-medium">{payload[1]?.value}</span>
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 mb-8">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Clicks */}
        <div className="bg-white rounded-xl border border-black p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {t('links.analytics.totalClicks', 'Total Clicks')}
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {(totalClicks ?? analytics.totalClicks).toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-primary-50 rounded-lg">
              <TrendingUp className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>

        {/* Unique Users */}
        <div className="bg-white rounded-xl border border-black p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {t('links.analytics.uniqueUsers', 'Unique Users')}
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {analytics.uniqueUsers.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Total Links */}
        <div className="bg-white rounded-xl border border-black p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {t('links.analytics.totalLinks', 'Total Links')}
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {totalLinks.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <LinkIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl border border-black p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('links.analytics.clicksOverTime', 'Clicks Over Time')}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {t('links.analytics.last30Days', 'Last 30 days performance')}
          </p>
        </div>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a5b4fc" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#a5b4fc" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                stroke="#6b7280" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke="#6b7280" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="clicks"
                stroke="#6366f1"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorClicks)"
              />
              <Area
                type="monotone"
                dataKey="uniqueUsers"
                stroke="#a5b4fc"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorUsers)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary-600 rounded-full"></div>
            <span className="text-sm text-gray-600">{t('links.analytics.clicks', 'Clicks')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary-300 rounded-full"></div>
            <span className="text-sm text-gray-600">{t('links.analytics.uniqueUsers', 'Unique Users')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinksMetrics; 